/**
 * THE ONE TRANSPORT. No component, hook or feature calls fetch() directly.
 *
 * Every request gets:
 *   - the base URL (api/config)
 *   - X-Auth-Token — the session token, when there is one (api/token). Rachel's
 *     decision 18-09-2026: a header token, not a cookie. `credentials` is
 *     therefore "omit": nothing here rides on a cookie, and the API is served
 *     from its own origin.
 *   - X-Correlation-Id — a fresh id the server logs beside its trace id
 *   - Idempotency-Key on every non-GET — a retry of the same act passes the same key,
 *     so the server can answer a replay with the original result
 *   - a timeout
 * and every failure is thrown as an AppError (api/problems).
 *
 * `apiJson` is for the few provisional calls made before the backend's OpenAPI
 * exists (the stub session). Once a module's document arrives, its calls go
 * through `createApiClient<paths>()`, which is openapi-fetch over this same transport.
 */
import createClient, { type Middleware } from "openapi-fetch";
import type { ZodType } from "zod";
import { apiBaseUrl, apiUrl } from "./config";
import { AppError, errorFromResponse } from "./problems";
import { authHeaderValue } from "./token";

export const DEFAULT_TIMEOUT_MS = 20_000;

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

export interface RequestOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Pass the same key when retrying the same act. Generated otherwise. */
  idempotencyKey?: string;
  timeoutMs?: number;
}

function newId(): string {
  return globalThis.crypto.randomUUID();
}

/** Adds the platform headers to a request's headers. Exported for the typed client's middleware. */
export function applyStandardHeaders(headers: Headers, method: string, idempotencyKey?: string): void {
  if (!headers.has("Accept")) headers.set("Accept", "application/json, application/problem+json");
  if (!headers.has("X-Correlation-Id")) headers.set("X-Correlation-Id", newId());
  const token = authHeaderValue();
  if (token !== null && !headers.has("X-Auth-Token")) headers.set("X-Auth-Token", token);
  if (!SAFE_METHODS.has(method.toUpperCase()) && !headers.has("Idempotency-Key")) {
    headers.set("Idempotency-Key", idempotencyKey ?? newId());
  }
}

/** fetch with a timeout, turning "no answer" into a typed AppError. */
async function send(request: Request, timeoutMs: number): Promise<Response> {
  const timeout = AbortSignal.timeout(timeoutMs);
  const signal = AbortSignal.any([request.signal, timeout]);
  try {
    return await fetch(request, { signal });
  } catch (cause) {
    if (timeout.aborted) throw new AppError("timeout", { cause });
    if (request.signal.aborted) throw cause; // the caller cancelled: not an error to report
    throw new AppError("network", { cause });
  }
}

/** A request whose failures are thrown as AppError. Resolves only with an ok response. */
export async function apiFetch(path: string, options: RequestOptions = {}): Promise<Response> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers = new Headers(options.headers);
  applyStandardHeaders(headers, method, options.idempotencyKey);
  let body: BodyInit | undefined;
  if (options.body !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(options.body);
  }
  const request = new Request(apiUrl(path), {
    method,
    headers,
    credentials: "omit",
    ...(body !== undefined ? { body } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });
  const response = await send(request, options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  if (!response.ok) throw await errorFromResponse(response);
  return response;
}

/**
 * A JSON request. With a schema, the body is validated: a response that does not
 * match is a CONTRACT error — the backend and this build disagree about a shape,
 * and saying so is better than rendering half a record.
 */
export async function apiJson<T>(
  path: string,
  options: RequestOptions = {},
  schema?: ZodType<T>,
): Promise<T> {
  const response = await apiFetch(path, options);
  if (response.status === 204) return undefined as T;
  let data: unknown;
  try {
    data = await response.json();
  } catch (cause) {
    throw new AppError("contract", {
      status: response.status,
      cause,
      message: `${path} did not return JSON`,
    });
  }
  if (!schema) return data as T;
  const parsed = schema.safeParse(data);
  if (!parsed.success) {
    throw new AppError("contract", {
      status: response.status,
      cause: parsed.error,
      message: `${path} returned a shape this build does not expect`,
    });
  }
  return parsed.data;
}

const standardHeaders: Middleware = {
  onRequest({ request }) {
    applyStandardHeaders(request.headers, request.method);
    return request;
  },
  async onResponse({ response }) {
    if (!response.ok) throw await errorFromResponse(response);
    return response;
  },
};

/**
 * The typed client for a module's generated `paths` (src/api/generated/<module>.ts).
 * Failures throw AppError, so a query or mutation sees one error shape whichever
 * path it took.
 */
export function createApiClient<Paths extends object>(timeoutMs = DEFAULT_TIMEOUT_MS) {
  const client = createClient<Paths>({
    baseUrl: apiBaseUrl(),
    credentials: "omit",
    fetch: (request: Request) => send(request, timeoutMs),
  });
  client.use(standardHeaders);
  return client;
}
